import { vm } from "./config.js";
import { textChat } from "../index.js";
import {
    isLocalMode,
    setLocalGlobalFlag,
    setLocalStopAll,
    executeModeChange,
} from "../vm/local-mode.js";
import { setLoadingProject, isPauseEventHandling } from "../vm/overrides.js";
import { precacheProjectAssets } from "../utils/project-assets.js";
import validateProject from "scratch-parser";
import {
    onSpriteAdd,
    onSpriteDelete,
    onSpriteRename,
} from "../sprites/index.js";
import {
    onSpriteDragStart,
    onSpriteDragPosition,
    onSpriteDragEnd,
} from "../sprites/drag.js";
import {
    onBitmapUpdate,
    onCostumeAdded,
    onCostumeDelete,
    onCostumeDuplicate,
    onCostumeReorder,
    onCostumeRename,
    onSvgUpdated,
} from "../sprites/costumes.js";
import { onBackdropAdded } from "../sprites/backdrops.js";
import {
    onSoundAdded,
    onSoundDelete,
    onSoundDuplicate,
    onSoundRename,
    onSoundReorder,
    onSoundUpdate,
} from "../sprites/sounds.js";
import {
    applyRemoteChange,
    onEndDrag,
    showPreviewBlock,
    onBlockSelected,
    onStackClicked,
} from "../blocks/index.js";
import {
    onSpriteEditorStateChange,
    onExtensionAdded,
    onMonitorAdded,
    onMonitorUpdated,
    onSliderValueChanged,
} from "../vm/index.js";
import { getPeer, updatePlayerCount } from "../ui/players.js";
import { showTopNotification } from "../ui/notification.js";
import { stopDemoTimer } from "../ui/demo-timer.js";
import { showMonitorEditor } from "../ui/monitor-editors.js";
import {
    onEditPosition,
    onRemoteArrowPlayersUpdate,
} from "../ui/remote-arrows.js";
import {
    onSpriteFocus,
    onPresencePlayersUpdate,
} from "../ui/target-presence.js";
import "../ui/target-presence-badges.js";

const guardedOn = (socket, event, handler) => {
    socket.on(event, (...args) => {
        try {
            const result = handler(...args);
            if (result instanceof Promise) {
                result.catch((e) =>
                    console.error(`Error handling "${event}" event:`, e),
                );
            }
        } catch (e) {
            console.error(`Error handling "${event}" event:`, e);
        }
    });
};

const logProjectLoadFailure = (error, data) => {
    const shape =
        typeof data === "string"
            ? `string of ${data.length} chars starting with ${JSON.stringify(data.slice(0, 60))}`
            : data && typeof data === "object"
              ? `object with keys [${Object.keys(data).join(", ")}]`
              : String(data);

    console.error("Error loading the project", error, `(received ${shape})`);

    try {
        const input = typeof data === "string" ? data : JSON.stringify(data);
        validateProject(input, false, (validationError) => {
            if (validationError)
                console.error(
                    "The project was rejected by the validator:",
                    JSON.stringify(validationError),
                );
        });
    } catch (e) {
        console.error("The project could not be validated again:", e);
    }
};

export const setupListeners = (socket) => {
    const on = (event, handler) => guardedOn(socket, event, handler);

    on("projectChange", (data) => {
        if (!isPauseEventHandling()) applyRemoteChange(data);
    });

    on("spriteStateChange", (data) => {
        onSpriteEditorStateChange(data);
    });

    on("extensionAdded", (data) => {
        onExtensionAdded(data);
    });

    on("monitorAdded", (data) => {
        onMonitorAdded(data);
    });

    on("monitorUpdated", (data) => {
        onMonitorUpdated(data);
        showMonitorEditor({
            monitorId: data.data?.monitor?.id,
            username: data.username,
            color: data.color,
            avatar: getPeer(data.uid)?.avatar,
        });
    });

    on("sliderValueChanged", (data) => {
        if (!onSliderValueChanged(data)) return;
        showMonitorEditor({
            monitorId: data.data?.variableId,
            username: data.username,
            color: data.color,
            avatar: getPeer(data.uid)?.avatar,
        });
    });

    on("spriteRename", (data) => {
        onSpriteRename(data);
    });

    on("costumeDuplicate", (data) => {
        onCostumeDuplicate(data);
    });

    on("spriteDragStart", (data) => {
        onSpriteDragStart(data);
    });

    on("spriteDragPosition", (data) => {
        onSpriteDragPosition(data);
    });

    on("spriteDragEnd", (data) => {
        onSpriteDragEnd(data);
    });

    on("spriteAdd", (data) => {
        onSpriteAdd(data);
    });

    on("spriteDelete", (data) => {
        onSpriteDelete(data);
    });

    on("bitmapUpdate", (data) => {
        onBitmapUpdate(data);
    });

    on("costumeAdded", (data) => {
        onCostumeAdded(data);
    });

    on("costumeReorder", (data) => {
        onCostumeReorder(data);
    });

    on("costumeRename", (data) => {
        onCostumeRename(data);
    });

    on("costumeDelete", (data) => {
        onCostumeDelete(data);
    });

    on("updatedSvg", (data) => {
        onSvgUpdated(data);
    });

    on("backdropAdded", (data) => {
        onBackdropAdded(data);
    });

    on("soundAdd", (data) => {
        onSoundAdded(data);
    });

    on("soundReorder", (data) => {
        onSoundReorder(data);
    });

    on("soundRename", (data) => {
        onSoundRename(data);
    });

    on("soundDuplicate", (data) => {
        onSoundDuplicate(data);
    });

    on("soundUpdate", (data) => {
        onSoundUpdate(data);
    });

    on("soundDelete", (data) => {
        onSoundDelete(data);
    });

    on("globalFlagPressed", (data) => {
        if (isLocalMode()) {
            showTopNotification(
                `¡${data.username} inició la ejecución! Ahora ves la ejecución de todos`,
                5500,
            );
            executeModeChange();
        }
        setLocalGlobalFlag(true);
        vm.greenFlag();
    });

    on("globalStopAllPressed", (data) => {
        if (isLocalMode()) executeModeChange();
        setLocalStopAll(true);
        vm.stopAll();
    });

    on("projectSync", async (data) => {
        setLoadingProject(true);

        try {
            precacheProjectAssets(vm, data);
            await vm.loadProject(data);
        } catch (e) {
            logProjectLoadFailure(e, data);
        }

        setTimeout(() => {
            setLoadingProject(false);
        }, 1000);
    });

    on("roomClosed", (data) => {
        stopDemoTimer();

        socket.disconnect();

        showTopNotification(
            `${data.reason ?? "La sala se ha cerrado."} Volviendo a la página principal…`,
        );
        setTimeout(() => window.location.replace(window.location.origin), 4000);
    });

    on("chat", (data) => {
        if (textChat) textChat.addMessage(data, data.color);
    });

    on("playerUpdate", (data) => {
        updatePlayerCount(data);
        onRemoteArrowPlayersUpdate(data.players);
        onPresencePlayersUpdate(data.players);
    });

    on("editPosition", (data) => {
        onEditPosition(data);
    });

    on("spriteFocus", (data) => {
        onSpriteFocus(data);
    });

    on("previewPosition", (data) => {
        showPreviewBlock(data);
    });

    on("endDrag", (data) => {
        onEndDrag();
    });

    on("blockSelected", (data) => {
        onBlockSelected(data);
    });

    on("stackClicked", (data) => {
        onStackClicked(data);
    });
};
